"use client";
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
} from "@mui/material";
import apiEndpoints from "../apiConfig";

function Membership() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    // Replace with your local PHP API URL
    fetch(`${apiEndpoints.subscriptionPlans}?action=membership`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          // Map backend fields to frontend display
          const formattedPlans = data.data.map((plan) => ({
            id: plan.id,
            title: plan.name,
            price: parseFloat(plan.price) || plan.price,
            originalPrice: plan.original_price
              ? parseFloat(plan.original_price) || plan.original_price
              : null,
            duration: `${plan.duration_months} months`,
            offers: plan.features || null,
            contacts: plan.contacts ? parseInt(plan.contacts) : null,
            messages: plan.messages ? parseInt(plan.messages) : null,
            viewProfiles: plan.view_profiles
              ? parseInt(plan.view_profiles)
              : null,
            featureProfile: plan.feature_profile_days
              ? parseInt(plan.feature_profile_days)
              : null,
          }));
          setPlans(formattedPlans);
        } else {
          setPlans([]);
        }
      })
      .catch((err) => console.error("Error fetching plans:", err))
      .finally(() => setLoading(false));
  }, []);

  const { user } = useAuth(); // Get user to check current plan
  const { showToast } = useToast();

  // Fetch Current Plan Details
  useEffect(() => {
    if (user?.user_id) {
      fetch(`${apiEndpoints.getUserPlan}?user_id=${user.user_id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success" && data.data) {
            setCurrentPlan(data.data);
          }
        })
        .catch((err) => console.error("Error fetching current plan:", err));
    }
  }, [user]);

  const handleBuyPlan = (plan) => {
    if (!user) {
      showToast("Please login to buy a plan", "error");
      return;
    }

    // Double check locally (API also checks)
    if (
      currentPlan &&
      currentPlan.plan_id == plan.id &&
      new Date(currentPlan.expiryDate) > new Date()
    ) {
      showToast("This plan is already active", "info");
      return;
    }

    // Call API to buy plan
    fetch(apiEndpoints.payments, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "buy_plan",
        user_id: user.user_id, // Ensure user object has user_id
        plan_id: plan.id,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showToast(data.message, "success");
          // Refresh current plan locally
          setCurrentPlan({
            plan_id: plan.id,
            planType: plan.title,
            expiryDate: data.expiryDate,
          });
          // Optionally update user context here if method exists
        } else if (data.status === "already_active") {
          showToast(data.message, "info");
        } else {
          showToast(data.message || "Payment failed", "error");
        }
      })
      .catch((err) => {
        console.error("Buy Plan Error:", err);
        showToast("Something went wrong. Please try again.", "error");
      });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography variant="h6">Loading plans...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        minHeight: "100vh",
        py: { xs: 3, sm: 4, md: 5 },
        px: { xs: 2, sm: 4, md: 6, lg: 10, xl: 16 },
      }}
    >
      <Typography
        variant="h5"
        align="center"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: "#333",
          fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
        }}
      >
        Choose any plan to continue
      </Typography>
      <Typography variant="body2" align="center" sx={{ mb: 4, color: "#555" }}>
        All these plans will include GST during the checkout
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // mobile → 1
            sm: "repeat(2, 1fr)", // tablet → 2
            md: "repeat(3, 1fr)", // small laptop → 3
            lg: "repeat(4, 1fr)", // large screen → 4
          },
          gap: 3,
          maxWidth: "1400px", // increase from 1000
          mx: "auto",
        }}
      >
        {plans.length > 0 ? (
          plans.map((plan, index) => {
            const isDark = index % 2 === 0;
            const isCurrent = currentPlan && currentPlan.plan_id == plan.id;

            const cardStyles = {
              background: isDark ? "#050505" : "#ffffff",
              border: isDark ? "none" : "2px solid rgba(255, 166, 0, 0.45)",
              color: isDark ? "#fddc4d" : "#1f1f1f",
              boxShadow: "none",
              borderRadius: 1,
            };

            // Build feature rows to display
            const featureRows = [
              { label: "Duration", value: plan.duration },
              plan.contacts != null && {
                label: "Contacts",
                value: `${plan.contacts} nos`,
              },
              plan.messages != null && {
                label: "Message",
                value: `${plan.messages} nos`,
              },
              plan.viewProfiles != null && {
                label: "View Profiles",
                value: `${plan.viewProfiles} nos`,
              },
              plan.featureProfile != null && {
                label: "Feature Profile",
                value: `${plan.featureProfile} days`,
              },
              plan.offers && { label: "Plan Offers", value: plan.offers },
            ].filter(Boolean);

            return (
              <Card
                key={index}
                sx={{
                  ...cardStyles,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "stretch",
                  minHeight: { xs: "auto", sm: 420 },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2.5, sm: 3 },
                  //     gridColumn:
                  //       index >= 4
                  //         ? { xs: "1", sm: "span 1", md: "2 / span 1" }
                  //         : undefined,
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    gap: 0.5,
                    textAlign: "left",
                    alignItems: "flex-start",
                    p: 0,
                    "&:last-child": { pb: 0 },
                  }}
                >
                  {/* Plan Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: isDark ? "#fddc4d" : "#e8a000",
                      mb: 0.5,
                    }}
                  >
                    {plan.title}
                  </Typography>

                  {/* Price Row with strikethrough */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        color: isDark ? "#ffffff" : "#1f1f1f",
                      }}
                    >
                      ₹{" "}
                      {typeof plan.price === "number"
                        ? plan.price.toLocaleString("en-IN")
                        : plan.price}
                    </Typography>
                    {plan.originalPrice && (
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: "line-through",
                          color: isDark
                            ? "rgba(255,255,255,0.45)"
                            : "rgba(0,0,0,0.4)",
                          fontSize: "0.95rem",
                        }}
                      >
                        ₹{" "}
                        {typeof plan.originalPrice === "number"
                          ? plan.originalPrice.toLocaleString("en-IN")
                          : plan.originalPrice}
                      </Typography>
                    )}
                  </Box>

                  <Divider
                    sx={{
                      borderColor: isDark
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(0,0,0,0.1)",
                      width: "100%",
                      my: 1,
                    }}
                  />

                  {/* Feature Rows */}
                  {featureRows.map((row, i) => (
                    <Box
                      key={i}
                      sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#111",
                          fontSize: "0.88rem",
                        }}
                      >
                        {row.label}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark
                            ? "rgba(255,255,255,0.8)"
                            : "rgba(0,0,0,0.7)",
                          fontSize: "0.88rem",
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>

                {/* Button */}
                <Box sx={{ mt: 2.5, width: "100%" }}>
                  <Button
                    variant="contained"
                    disabled={isCurrent}
                    onClick={() => !isCurrent && handleBuyPlan(plan)}
                    sx={{
                      width: "100%",
                      borderRadius: 1,
                      paddingY: 1.5,
                      fontWeight: 700,
                      fontSize: "1rem",
                      textTransform: "none",
                      background: isCurrent ? "#34a853" : "#fddc4d",
                      backgroundColor: isCurrent ? "#34a853" : "#fddc4d",
                      backgroundImage: "none !important",
                      color: isCurrent ? "#fff" : "#0b0b0b",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: isCurrent ? "#34a853" : "#fddc4d",
                        backgroundImage: "none",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#34a853",
                        color: "#fff",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isCurrent ? "Current Plan" : "Buy Plan"}
                  </Button>
                </Box>
              </Card>
            );
          })
        ) : (
          <Typography>No plans available</Typography>
        )}
      </Box>
    </Box>
  );
}

export default Membership;
